import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { MapPin, ChevronDown, Plus, Check } from 'lucide-react';
import MapAddressPicker from './MapAddressPicker';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { useLoginToContinue } from '../../hooks/useLoginToContinue';
import { isSessionAuthError, takeLoginIntent } from '../../utils/loginIntent';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const LOGIN_TO_ADD_ADDRESS = 'Please log in to add an address.';

const AddressSelector = ({ selectedAddress, onSelect }) => {
  const { user } = useAuth();
  const location = useLocation();
  const loginToContinue = useLoginToContinue();
  const [open, setOpen] = useState(false);
  const [showMapPicker, setShowMapPicker] = useState(false);
  // An address typed before a login interruption, to pre-fill the picker with.
  const [draftAddress, setDraftAddress] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);

  // Addresses belong to an account: send the customer to log in, then back
  // here to carry on (see utils/loginIntent), instead of letting the save fail.
  const loginToAddAddress = (draft = null) =>
    loginToContinue({ message: LOGIN_TO_ADD_ADDRESS, action: 'add-address', draft });

  // Back from logging in: reopen Add Address, with anything already typed.
  useEffect(() => {
    if (!user) return;
    const intent = takeLoginIntent(location.pathname);
    if (intent?.action === 'add-address') {
      setDraftAddress(intent.draft || null);
      setShowMapPicker(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadAddresses = async () => {
    if (!user) return; // a guest has no saved addresses — the request would only be refused
    setLoading(true);
    try {
      const res = await axios.get(`${API}/customer/addresses`);
      setAddresses(res.data || []);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) loadAddresses();
    // Runs when the dropdown opens; loadAddresses reads the current user then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSelectAddress = (addr) => {
    onSelect(addr);
    setOpen(false);
  };

  const handleSaveNewAddress = async (form) => {
    try {
      await axios.post(`${API}/customer/addresses`, { ...form, is_default: addresses.length === 0 });
      toast.success('Address saved!');
      setShowMapPicker(false);
      loadAddresses();
      // Select the newly added address
      const res = await axios.get(`${API}/customer/addresses`);
      const latest = res.data?.[res.data.length - 1];
      if (latest) onSelect(latest);
      setOpen(false);
    } catch (error) {
      if (isSessionAuthError(error)) {
        // The session is no longer valid (expired / signed out elsewhere):
        // log in again and come back with this address pre-filled.
        setShowMapPicker(false);
        loginToAddAddress(form);
        return;
      }
      toast.error(error.response?.data?.detail || 'Failed to save address');
    }
  };

  const handleAddNewAddress = () => {
    setOpen(false);
    if (!user) {
      loginToAddAddress();
      return;
    }
    setDraftAddress(null);
    setShowMapPicker(true);
  };

  const displayText = selectedAddress?.full_address
    ? selectedAddress.full_address.length > 35
      ? selectedAddress.full_address.substring(0, 35) + '...'
      : selectedAddress.full_address
    : 'Set delivery address';

  // Full-screen map picker — portal to document.body to escape header stacking context
  if (showMapPicker) {
    return createPortal(
      <div className="fixed inset-0 z-[9999] bg-white" data-testid="map-picker-overlay">
        <MapAddressPicker
          initialAddress={draftAddress}
          onSave={handleSaveNewAddress}
          onClose={() => { setShowMapPicker(false); setDraftAddress(null); }}
        />
      </div>,
      document.body
    );
  }

  return (
    <div className="relative" data-testid="address-selector">
      {/* Trigger */}
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 max-w-full"
        data-testid="address-bar"
      >
        <MapPin className="w-3.5 h-3.5 text-[var(--theme-primary)] shrink-0" />
        <span className="text-xs text-[var(--theme-text-muted)] truncate">{displayText}</span>
        <ChevronDown className={`w-3 h-3 text-[var(--theme-text-muted)] shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-100 z-40 overflow-hidden" data-testid="address-dropdown">
            <div className="px-3 py-2 border-b border-gray-50">
              <span className="text-xs font-semibold text-gray-400 uppercase">Deliver to</span>
            </div>

            <div className="max-h-60 overflow-y-auto">
              {loading ? (
                <div className="px-4 py-6 text-center text-sm text-gray-400">Loading...</div>
              ) : addresses.length === 0 ? (
                <div className="px-4 py-4 text-center text-sm text-gray-400" data-testid="address-dropdown-empty">
                  {user ? 'No saved addresses' : 'Log in to use your saved addresses'}
                </div>
              ) : (
                addresses.map(addr => (
                  <button
                    key={addr.id}
                    onClick={() => handleSelectAddress(addr)}
                    className="w-full flex items-start gap-3 px-3 py-3 hover:bg-gray-50 text-left border-b border-gray-50 last:border-0"
                    data-testid={`addr-option-${addr.id}`}
                  >
                    <MapPin className="w-4 h-4 text-[var(--theme-text-muted)] mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-medium text-gray-900">{addr.label}</span>
                        {selectedAddress?.id === addr.id && <Check className="w-3.5 h-3.5 text-[var(--theme-primary)]" />}
                      </div>
                      <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">{addr.full_address}</p>
                    </div>
                  </button>
                ))
              )}
            </div>

            <button
              onClick={handleAddNewAddress}
              className="w-full flex items-center gap-2 px-3 py-3 border-t border-gray-100 text-[var(--theme-primary)] hover:bg-[var(--theme-primary)]/5 text-sm font-medium"
              data-testid="add-new-address-dropdown-btn"
            >
              <Plus className="w-4 h-4" /> Add New Address
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AddressSelector;
